import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const BookmarkContext = createContext();

export const BookmarkProvider = ({ children }) => {
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const saved = localStorage.getItem('gita_bookmarks');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Failed to load bookmarks', e);
      return [];
    }
  });

  const [notes, setNotes] = useState(() => {
    try {
      const saved = localStorage.getItem('gita_notes');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error('Failed to load notes', e);
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('gita_bookmarks', JSON.stringify(bookmarks));
    } catch (e) {
      console.error('Failed to save bookmarks', e);
    }
  }, [bookmarks]);

  useEffect(() => {
    try {
      localStorage.setItem('gita_notes', JSON.stringify(notes));
    } catch (e) {
      console.error('Failed to save notes', e);
    }
  }, [notes]);

  const toggleBookmark = useCallback((chapter, verse, verseData = null) => {
    const key = `${chapter}:${verse}`;
    setBookmarks(prev => {
      const exists = prev.some(item => item.key === key);
      if (exists) {
        return prev.filter(item => item.key !== key);
      }
      return [
        ...prev,
        {
          key,
          chapter: String(chapter),
          verse: String(verse),
          data: verseData,
          savedAt: new Date().toISOString()
        }
      ];
    });
  }, []);

  const isBookmarked = useCallback((chapter, verse) => {
    const key = `${chapter}:${verse}`;
    return bookmarks.some(item => item.key === key);
  }, [bookmarks]);

  const saveNote = useCallback((chapter, verse, noteText) => {
    const key = `${chapter}:${verse}`;
    setNotes(prev => ({
      ...prev,
      [key]: {
        text: noteText,
        updatedAt: new Date().toISOString()
      }
    }));
  }, []);

  const getNote = useCallback((chapter, verse) => {
    const key = `${chapter}:${verse}`;
    return notes[key]?.text || '';
  }, [notes]);

  const value = useMemo(() => ({
    bookmarks,
    toggleBookmark,
    isBookmarked,
    saveNote,
    getNote,
    notesCount: bookmarks.length
  }), [bookmarks, toggleBookmark, isBookmarked, saveNote, getNote]);

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
};

export const useBookmarks = () => {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error('useBookmarks must be used within a BookmarkProvider');
  }
  return context;
};
